import { defineStore } from 'pinia';  
import { nextTick } from 'vue';
import type GuestInfo from '../types/GuestInfo'
import type Basket from '../classes/Basket';
import BasketProduct from '../classes/BasketProduct';
import type Order from '../classes/Order';
import type OrderDetails from '../classes/OrderDetails';
import BasketService from '../services/Basket';
import CateringOrdersService from '../services/CateringOrders';
import OrdersService from '../services/Orders';
import { useUserStore } from './user';
import { useMenuStore } from './menu';
import { useFlashMessageStore } from './flashMessages';
import type FlashMessage from '../types/FlashMessage';
import type LoyaltyScheme from '../classes/LoyaltyScheme';
import type Location from '../classes/Location';
import type AvailableTime from '../types/AvailableTime';
import type BillingScheme from '../types/BillingScheme';
import type Product from '../classes/Product';
import type Reward from '../types/Reward';
import type DerivedDiscount from '../types/DerivedDiscount';
import type LoyaltyReward from '../types/LoyaltyReward';
import type AppliedReward from '../types/AppliedReward';
import type CateringOrder from '../types/CateringOrder';
import type OrderProduct from '../types/OrderProduct';
import { useLocationsStore } from './locations';
import { useCateringOrderStore } from './catering_order';
import { plainToClass } from 'class-transformer';
import { DateTime } from 'luxon';
import type ChoiceCustomFieldInterface from '@/types/ChoiceCustomField';

export const useBasketStore = defineStore({
  id: "basketStore",
  state: () => ({
    order: null as (OrderDetails | null),
    basket: null as (Basket | null),
    sessionBasketId: null as (string | null),
  }),
  getters: {
    itemCount(state): number {
      // Loop over products in the basket and get their quantity
      if (this.basket && this.basket.products) {
        let count = 0;
        this.basket.products.forEach((product) => {
          count += product.quantity;
        })

        return count;
      } else {
        return 0
      }
    },
    currentDiscounts(state): DerivedDiscount[] {
      if (this.basket && this.basket.deriveddiscounts) {
        return this.basket.deriveddiscounts;
      } else {
        return [] as DerivedDiscount[];
      }
    },
  },
  actions: {
    mergeValidationBasket(validatedBasket: Basket) {
      if (this.basket && validatedBasket) {
         this.basket.salestax = validatedBasket.tax
         this.basket.deriveddiscounts = validatedBasket.deriveddiscounts;
         this.basket.subtotal = validatedBasket.subtotal
         this.basket.total = validatedBasket.total
         this.basket.totalfees = validatedBasket.totalfees
      }
    },
    processBasketProducts(basketProducts: BasketProduct[], basketLocation: number): Promise<any> {
      // Takes an array of basketProducts, sends back an updated array of basketProducts
      const menuStore = useMenuStore();
      const locationStore = useLocationsStore();  

      return new Promise((resolve, reject) => {
        // If the basket has products we need to loop over those products and tack on the full product data
        if (basketProducts) {
          const bps = [] as BasketProduct[];
          // The basket has an olo ID but we needs to grab the unit id for menu
          locationStore.getLocationById(basketLocation).then((loc: Location) => {
            menuStore.getIdKeyedProducts(parseFloat(loc.extref)).then((products) => {
              basketProducts.forEach((bp) => {
                let prod = plainToClass(BasketProduct, (bp as object), { excludeExtraneousValues: true })

                let prodId = (typeof(prod.productId) != undefined) ? prod.productId : prod.id;
                if (prodId) {
                  let match = products.get(prodId.toString());
                  if (match) {
                    prod.metadata = match.metadata;
                    prod.images = match.images;
                    prod.chainproductid = match.chainproductid;
                    prod.feeds = match.feeds;
                    prod.custom_data = match.custom_data;
                    prod.description = match.description;
                  } else {
                    prod.metadata = [];
                    prod.images = [];
                    prod.feeds = [0,0,0];
                    prod.description = "";
                  }
                }
                bps.push(prod);
              })

              resolve(bps);
            });
          })
        }

      })
    },
    createBasket(): Promise<any> {
      const userStore = useUserStore();
      const cateringOrderStore = useCateringOrderStore();

      return new Promise((resolve, reject) => {
        if (userStore.userLocation) {
          BasketService.createBasket(userStore.userLocation!.id).then((basket: Basket) => {
            localStorage.setItem('mypiadaBasketId', basket.id);
            BasketService.setMypiadaBasket(basket.id, basket.vendorid);

            // If we have a cateringOrder already, update it to have the new basket ID
            if (cateringOrderStore.cateringOrder) {
              cateringOrderStore.cateringOrder.basket_id = basket.id;
              cateringOrderStore.updateCateringOrder(cateringOrderStore.cateringOrder.id, cateringOrderStore.cateringOrder);
            }

            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              resolve(basket);
            });
          });
        } else {
          // TODO: How do error?
          alert('Error: Location has not been set.');
          reject("No location has been set")
        }
      });
    }, 
    fetchBasket() {
      const userStore = useUserStore()
      const basketId = localStorage.getItem('mypiadaBasketId');

      if (basketId) {
        BasketService.getBasket(basketId).then((basket: Basket) => {
          this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
            basket.products = processedBasketProducts;
            this.basket = basket;
          });

          if (this.sessionBasketId != basket.id) {
            BasketService.setMypiadaBasket(basket.id, basket.vendorid);
          }

          localStorage.setItem('mypiadaBasketId', basket.id);
        }).catch((error: Error) => {
          BasketService.setMypiadaBasket(undefined);
          localStorage.removeItem('mypiadaBasketId');
        });
      }
    },
    fetchBasketWithPromise(basketId: string): Promise<any> {
      const userStore = useUserStore()
      return new Promise((resolve, reject) => {
        if (basketId) {
          BasketService.getBasket(basketId).then((basket: Basket) => {
            if (this.sessionBasketId != basket.id) {
              BasketService.setMypiadaBasket(basket.id);
            }

            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              resolve(basket);
            });

            if (this.sessionBasketId != basket.id) {
              BasketService.setMypiadaBasket(basket.id, basket.vendorid);
            }

            localStorage.setItem('mypiadaBasketId', basket.id);
          }).catch((error: Error) => {
            BasketService.setMypiadaBasket(undefined);
            localStorage.removeItem('mypiadaBasketId');
            reject(error);
          });
        } else {
          reject(new Error('Basket not found'));
        }
      });
    },
    clearBasket(): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.setMypiadaBasket(undefined).then(() => {
          this.basket = null as (Basket | null);
          localStorage.removeItem('mypiadaBasketId');
          resolve('');
        }).catch((error: Error) => {
          reject(error)
        })
      })
    },
    //--------------------------------------------------------------------------------------
    //
    // Generic Olo Orders
    //
    //--------------------------------------------------------------------------------------

    getOrder(id: string): Promise<any> {
      return new Promise((resolve, reject) => {
        OrdersService.getOrder(id).then((order: OrderDetails) => {
          this.processBasketProducts(order.products, order.vendorid).then((processedBasketProducts) => {
            this.basket = null; // Clear out the basket
            order.products = processedBasketProducts;
            this.order = order;
            resolve(this.order);
          });
        });
      });
    },
    cancelOrder():Promise<any> {
      return new Promise((resolve, reject) => {
        if (this.order && this.order.id) {
          OrdersService.cancelOrder(this.order!.id).then((order: OrderDetails) => {
            // Should return the order details but now status should be set to cancelled?
            this.processBasketProducts(order.products, order.vendorid).then((processedBasketProducts) => {
              this.basket = null; // Clear out the basket
              order.products = processedBasketProducts;
              this.order = order;

              localStorage.removeItem('orderId'); // clean up

              resolve(this.order);
            });
          });
        }
      });
    },

    //--------------------------------------------------------------------------------------
    //
    // Generic Olo Orders
    //
    //--------------------------------------------------------------------------------------

    reorder(id: string): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.reorder(id).then((basket: Basket) => {
          const locationsStore = useLocationsStore();
          const userStore = useUserStore();
          
          if (locationsStore.locations) {
            let locationObj = locationsStore.locations.find((loc: Location) => {
              return loc.id == basket.vendorid
            })

            if (locationObj) {            
              userStore.userLocation = locationObj
            } else {
              // Not really sure what to do here, shouldn't happen unless the store closes or 
              // User has demo vendor orders
            }
          } else {
            // We're going to have to fetch locations first
          }


          this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
            basket.products = processedBasketProducts;
            this.basket = basket;
          });

          if (this.sessionBasketId != basket.id) {
            BasketService.setMypiadaBasket(basket.id, basket.vendorid);
          }

          if (basket.catering_order != null && basket.catering_order.id != null) {
            const cateringOrder: CateringOrder = basket.catering_order;
            const cateringOrderStore = useCateringOrderStore();
            localStorage.setItem('cateringOrder', cateringOrder.id.toString());
            cateringOrderStore.cateringOrder = cateringOrder;
            cateringOrderStore.suggestions = null; // The order was created, so make sure we can get updated suggestions
          }

          localStorage.setItem('mypiadaBasketId', basket.id);

          resolve(basket);
        }).catch((error: Error) => {
          BasketService.setMypiadaBasket(undefined);
          localStorage.removeItem('mypiadaBasketId');
        });
      });
    },

    //--------------------------------------------------------------------------------------
    //
    // Products
    //
    // -------------------------------------------------------------------------------------

    addProduct(pid: string, qty: number, specialInstructions: string, choices: number[], choicecustomfields: ChoiceCustomFieldInterface[], trackSuggestion?: string): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();

      return new Promise((resolve, reject) => {
        if (this.basket) {
          BasketService.addProduct(this.basket.id, pid, qty, specialInstructions, choices, choicecustomfields).then((basket: Basket) => {
            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              // if (trackSuggestion) {
              //   cateringOrderStore.trackSuggestionConversion(trackSuggestion, pid, qty, specialInstructions, choices, choicecustomfields).then(() => {});
              // }

              resolve(basket);
            });
          }).catch((error: Error) => {
            reject(error)
          });
        } else {
          // User doesn't have a basket yet to add the product to
          this.createBasket().then((basket) => {
            if (basket) {
              BasketService.addProduct(basket.id, pid, qty, specialInstructions, choices, choicecustomfields).then((basket: Basket) => {
                this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
                  basket.products = processedBasketProducts;
                  this.basket = basket;
                  resolve(basket);
                });
              }).catch((error: Error) => {
                reject(error)
              });
            }
          });
        }
      });
    },
    addMultipleProducts(products: OrderProduct[], trackSuggestion?: string): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();

      return new Promise((resolve, reject) => {
        if (this.basket) {
          BasketService.addMultipleProducts(this.basket.id, products).then((basket: Basket) => {
            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              if (trackSuggestion) {
                products.forEach((prod: OrderProduct) => {
                  cateringOrderStore.trackSuggestionConversion(trackSuggestion, prod).then(() => {});
                })
              }

              resolve(basket);
            });
          }).catch((error: Error) => {
            reject(error)
          });
        } else {
          // User doesn't have a basket yet to add the product to
          this.createBasket().then((basket) => {
            if (basket) {
              BasketService.addMultipleProducts(this.basket!.id, products).then((basket: Basket) => {
                this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
                  basket.products = processedBasketProducts;
                  this.basket = basket;
                  resolve(basket);
                });
              }).catch((error: Error) => {
                reject(error)
              });
            }
          });
        }
      });
    },
    removeProduct(bpid: number): Promise<any> {
      const userStore = useUserStore();
      return new Promise((resolve, reject) => {
        if (this.basket) {
          BasketService.removeProduct(this.basket.id, bpid).then((basket: Basket) => {
            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              resolve(basket);
            });
          });
        }
      })
    },
    updateProduct(pid: string, bpid: string, qty: number, specialInstructions: string, choices: number[], choicecustomfields: ChoiceCustomFieldInterface[]): Promise<any> {
      const userStore = useUserStore();
      const flashMessageStore = useFlashMessageStore();

      return new Promise((resolve, reject) => {
        if (this.basket) {
          BasketService.updateProduct(this.basket.id, pid, bpid, qty, specialInstructions, choices).then((basket: Basket) => {
            const message = {
                id: Math.round(Date.now() / 1000),
                messageType: "success",
                text: "Product has been updated",
                headline: "",
                delay: 5000, // ms
                icon: "",
                cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              resolve(basket);
            });
          }).catch((error: Error) => {
            reject(error)
          });
        }
      });
    },
    fetchUpsells(): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.fetchUpsells(this.basket!.id).then((upsells: Product[]) => {
          resolve(upsells);
        }).catch((error:Error) => {
          reject(error);
        });
      });
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Information
    //
    // -------------------------------------------------------------------------------------

    getBillingSchemes(): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.getBillingSchemes(this.basket!.id).then((schemes: BillingScheme[]) => {
          resolve(schemes);
        })
      })
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Times
    //
    // -------------------------------------------------------------------------------------

    getAvailableTimes(start: DateTime): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.getAvailableTimes(this.basket!.id, start).then((times: AvailableTime[]) => {
          resolve(times);
        })
      })
    },
    setNextAvailableTime(start: DateTime, validate: boolean): Promise<any> {
      const store = this;
      return new Promise((resolve, reject) => {
        // First go grab the next available time
        store.getAvailableTimes(start).then((times: AvailableTime[]) => {
          // Use the next one
          const nextTime = DateTime.fromFormat(times[0].time, 'yyyyMMdd hh:mm');

          store.setTimeWanted(nextTime, validate).then(() => {
            resolve('')
          }).catch((error: Error) => {
            const nextNextTime = DateTime.fromFormat(times[1].time, 'yyyyMMdd hh:mm');
            store.setTimeWanted(nextNextTime, false).then(() => {
              resolve('')
            }).catch((error: Error) => {
              reject(error)
            })
          })
        })
      })
    },
    setTimeWanted(date: DateTime, validate: boolean): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();
      return new Promise((resolve, reject) => {
        BasketService.setTimeWanted(this.basket!.id, date).then((localBasket: Basket) => {
          // If the time wanted was updated, and we have a catering order; update it to match
          if (cateringOrderStore.cateringOrder && cateringOrderStore.cateringOrder.time_wanted) {
            let updatedCateringOrder = cateringOrderStore.cateringOrder;
            updatedCateringOrder.time_wanted = date.toISO() as string;
            cateringOrderStore.updateCateringOrder(updatedCateringOrder.id, updatedCateringOrder);
          }

          if (validate) {
            this.validateBasket(false).then((validatedBasket: Basket) => {
              this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
                localBasket.products = processedBasketProducts;
                this.basket = localBasket;
                this.mergeValidationBasket(validatedBasket);

                resolve(this.basket);
              });
            }).catch((error: any) => {
              // Kick the error back out
              reject(error)
            })
          } else {
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;

              resolve(this.basket);
            });
          }
        }).catch((error: Error) => {
          reject(error)
        });
      });
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Rewards
    //
    // -------------------------------------------------------------------------------------

    fetchEligibleRewards(): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.fetchEligibleRewards(this.basket!.id).then((rewards: Reward[]) => {
          resolve(rewards);
        }).catch((error:Error) => {
          reject(error);
        });
      });
    },
    applyRewards(): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return new Promise((resolve, reject) => {
        this.fetchEligibleRewards().then((rewards: LoyaltyReward[]) => {
          if (rewards && rewards.length > 0) {
            const reward = rewards![0];

            BasketService.applyRewards(this.basket!.id, reward.membershipid, reward.reference).then((localBasket: Basket) => {
              this.validateBasket(false).then((validatedBasket: Basket) => {
                const message = {
                  id: Math.round(Date.now() / 1000),
                  messageType: "success",
                  text: "Rewards have been applied",
                  headline: "",
                  delay: 5000, // ms
                  icon: "",
                  cssClass: "",
                } as FlashMessage;
                flashMessageStore.addMessage(message);

                this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
                  localBasket.products = processedBasketProducts;
                  this.basket = localBasket;
                  this.mergeValidationBasket(validatedBasket);

                  resolve(this.basket);
                });
              }).catch((error: Error) => {
                // Validation failed but the loyalty was still applied so resolve the basket
                this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
                  localBasket.products = processedBasketProducts;
                  this.basket = localBasket;
                  reject(error);
                });
              })
            }).catch((error: Error) => {
              reject(error)
            });
          } else {
            reject(new Error('Rewards could not be applied.'))
          }
        });
      })
    },
    removeRewards(): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return new Promise((resolve, reject) => {
        const reward = this.basket!.appliedrewards![0];

        BasketService.removeRewards(this.basket!.id, reward.rewardid).then((localBasket: Basket) => {
          this.validateBasket(false).then((validatedBasket: Basket) => {
            const message = {
              id: Math.round(Date.now() / 1000),
              messageType: "success",
              text: "Rewards have been removed",
              headline: "",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              this.mergeValidationBasket(validatedBasket);

              resolve(this.basket);
            });
            resolve(this.basket);
          }).catch((error: Error) => {
            // Validation failed but the loyalty was still removed so resolve the basket
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              reject(error);
            });
          })
        }).catch((error: Error) => {
          reject(error)
        });
      });          
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Promos
    //
    // -------------------------------------------------------------------------------------

    applyPromo(couponcode: string): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return new Promise((resolve, reject) => {
        BasketService.applyPromo(this.basket!.id, couponcode).then((localBasket: Basket) => {
          this.validateBasket(false).then((validatedBasket: Basket) => {
            const message = {
              id: Math.round(Date.now() / 1000),
              messageType: "success",
              text: "Promo has been applied",
              headline: "",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              this.mergeValidationBasket(validatedBasket);

              resolve(this.basket);
            });
          }).catch((error: Error) => {
            // Validation failed but the promo was still applied so resolve the basket
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;

              reject(error);
            });
          })
        }).catch((error: Error) => {
          reject(error)
        });
      });
    },
    removePromo(): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return new Promise((resolve, reject) => {
        const reward = this.basket!.appliedrewards![0];
        BasketService.removePromo(this.basket!.id).then((localBasket: Basket) => {
          this.validateBasket(false).then((validatedBasket: Basket) => {
            const message = {
              id: Math.round(Date.now() / 1000),
              messageType: "success",
              text: "Rewards have been removed",
              headline: "",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              this.mergeValidationBasket(validatedBasket);
              resolve(this.basket);
            });
          }).catch((error: Error) => {
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              reject(error);
            });
          })
        }).catch((error: Error) => {
          reject(error)
        });
      });          
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Handoff Mode
    //
    // -------------------------------------------------------------------------------------

    applyDelivery(streetaddress: string, city: string, building: string, zipcode: string, specialinstructions: string): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.applyDelivery(this.basket!.id, streetaddress, city, building, zipcode, specialinstructions).then((localBasket: Basket) => {
          this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
            localBasket.products = processedBasketProducts;
            this.basket = localBasket;

            resolve(this.basket);
          });
        }).catch((error: Error) => {
          reject(error)
        });
      });
    },
    setTip(amount: number): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.setTip(this.basket!.id, amount).then((localBasket: Basket) => {
          this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
            localBasket.products = processedBasketProducts;
            this.basket = localBasket;

            resolve(this.basket);
          });
        }).catch((error: Error) => {
          reject(error)
        });
      });
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Validate and Submit
    //
    // -------------------------------------------------------------------------------------

    validateBasket(silently: boolean): Promise<any> {
      return new Promise((resolve, reject) => {
        let localBasket = this.basket;

        BasketService.validateBasket(this.basket!.id).then((vBasket: Basket) => {
          this.mergeValidationBasket(vBasket!);
          resolve(this.basket);
        }).catch((error: any) => {
          reject(error);
        })
      });
    },

    submitOrder(order: Order): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();
      order.orderref = this.basket!.id
      return new Promise((resolve, reject) => {
        BasketService.submitOrder(this.basket!.id, order).then((order: OrderDetails) => {
          this.processBasketProducts(order.products, (this.basket! as Basket).vendorid).then((processedBasketProducts) => {
            localStorage.removeItem('mypiadaBasketId');
            localStorage.removeItem('cateringOrder');
            localStorage.setItem('orderId', order.id);
            cateringOrderStore.cateringOrder = null as (CateringOrder | null);

            this.basket = null; // Clear out the basket
            order.products = processedBasketProducts;
            this.order = order;
            this.clearBasket().then(() => {
              resolve(this.order);
            })
          });
        }).catch((error: Error) => {
          reject(error)
        })
      });
    },
  }
})
